import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationsAPI } from '../services/api';
import { Bell, Check, CheckCheck, Layers, Scissors, CircleDot, Settings, ArrowLeft, Smartphone } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

const POLL_INTERVAL = 12000;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ pressing: true, cutting: true, boring: true });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const dropdownRef = useRef(null);
  const { isSubscribed, isSupported, permission, subscribe, unsubscribe } = usePushNotifications();

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationsAPI.getAll();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  const fetchPreferences = useCallback(async () => {
    try {
      const data = await notificationsAPI.getPreferences();
      setPrefs(data);
    } catch (err) {
      console.error('Failed to fetch notification preferences:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchPreferences]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setShowPrefs(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkRead = async (notifId) => {
    try {
      await notificationsAPI.markRead(notifId);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
    setLoading(false);
  };

  const handleTogglePref = async (key) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSavingPrefs(true);
    try {
      await notificationsAPI.updatePreferences(updated);
    } catch (err) {
      setPrefs(prefs); // revert on error
    }
    setSavingPrefs(false);
  };

  const getIcon = (type) => {
    if (type?.includes('pressing')) return <Layers className="w-3.5 h-3.5 text-orange-500" />;
    if (type?.includes('cutting')) return <Scissors className="w-3.5 h-3.5 text-blue-500" />;
    if (type?.includes('boring')) return <CircleDot className="w-3.5 h-3.5 text-purple-500" />;
    return <Bell className="w-3.5 h-3.5 text-gray-500" />;
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const prefItems = [
    { key: 'pressing', label: 'Pressing Updates', icon: <Layers className="w-4 h-4 text-orange-500" />, color: 'orange' },
    { key: 'cutting', label: 'Cutting Updates', icon: <Scissors className="w-4 h-4 text-blue-500" />, color: 'blue' },
    { key: 'boring', label: 'Boring Updates', icon: <CircleDot className="w-4 h-4 text-purple-500" />, color: 'purple' },
  ];

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      <button
        onClick={() => { setIsOpen(!isOpen); setShowPrefs(false); }}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="notification-bell-button"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] overflow-hidden" data-testid="notification-dropdown">
          {showPrefs ? (
            <>
              {/* Preferences View */}
              <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b">
                <button onClick={() => setShowPrefs(false)} className="p-1 hover:bg-gray-200 rounded" data-testid="prefs-back-btn">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <h3 className="text-sm font-semibold text-gray-800">Notification Preferences</h3>
              </div>
              <div className="p-4 space-y-3" data-testid="notification-preferences">
                {/* Push Notifications Toggle */}
                {isSupported && (
                  <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Smartphone className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700">Push Notifications</span>
                          <p className="text-xs text-gray-500">
                            {permission === 'denied' ? 'Blocked by browser' : isSubscribed ? 'Active' : 'Get alerts on your device'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={async () => { isSubscribed ? await unsubscribe() : await subscribe(); }}
                        disabled={permission === 'denied'}
                        className={`relative w-10 h-5 rounded-full transition-colors ${isSubscribed ? 'bg-blue-500' : permission === 'denied' ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-300'}`}
                        data-testid="push-toggle"
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isSubscribed ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-500 mb-3">Choose which operator updates you want to receive notifications for:</p>
                {prefItems.map(({ key, label, icon }) => (
                  <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">{icon}</div>
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                    </div>
                    <button
                      onClick={() => handleTogglePref(key)}
                      disabled={savingPrefs}
                      className={`relative w-10 h-5 rounded-full transition-colors ${prefs[key] ? 'bg-green-500' : 'bg-gray-300'}`}
                      data-testid={`pref-toggle-${key}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${prefs[key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Notifications View */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                <h3 className="text-sm font-semibold text-gray-800">
                  Notifications {unreadCount > 0 && <span className="text-red-500">({unreadCount})</span>}
                </h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllRead} disabled={loading} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="mark-all-read-btn">
                      <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                    </button>
                  )}
                  <button onClick={() => setShowPrefs(true)} className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors" title="Preferences" data-testid="notification-settings-btn">
                    <Settings className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                {notifications.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 text-sm">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No notifications yet
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${!notif.is_read ? 'bg-blue-50/50' : ''}`}
                      onClick={() => !notif.is_read && handleMarkRead(notif.id)}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      <div className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                        {getIcon(notif.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold text-gray-700 truncate">{notif.title}</p>
                          {notif.job_card_number && (
                            <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-mono flex-shrink-0">{notif.job_card_number}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-gray-400 mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      {!notif.is_read && (
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full" />
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
